export const logAuthEvent = (event: string, meta?: any) => {
    console.log(`[AUTH EVENT]: ${event}`, meta || '');
    // Future integration: send to Supabase 'events' table or external analytics like PostHog
    // await supabase.from('events').insert({ event_name: event, metadata: meta });
};

/**
 * Fire a Google Analytics event (no-op when gtag is absent or consent denied —
 * gtag.js handles consent gating itself via Consent Mode v2).
 *
 * `transport_type: 'beacon'` is forced so events survive full-page navigations
 * (OAuth returns, Stripe redirects) that would otherwise drop an in-flight hit.
 */
/**
 * GA4 reads these event parameters as traffic-source overrides, so an event that
 * sends `source: 'paywall'` re-attributes the whole session to "paywall / (not set)".
 * begin_checkout did exactly that, which hid the organic source of every checkout.
 */
const RESERVED_ATTRIBUTION_PARAMS = new Set(['source', 'medium', 'campaign', 'term', 'content']);

/** Rename reserved keys (source → source_param) instead of dropping the data. */
export const sanitizeEventParams = (params: Record<string, unknown> = {}): Record<string, unknown> =>
    Object.fromEntries(
        Object.entries(params).map(([key, value]) =>
            [RESERVED_ATTRIBUTION_PARAMS.has(key) ? `${key}_param` : key, value]
        )
    );

export const trackGaEvent = (eventName: string, params?: Record<string, unknown>) => {
    try {
        const gtag = (window as any).gtag;
        if (typeof gtag === 'function') {
            gtag('event', eventName, { transport_type: 'beacon', ...sanitizeEventParams(params) });
        }
    } catch {
        // analytics must never break the app
    }
};

/** Typed alias — the canonical way to fire a product event. */
export const trackEvent = trackGaEvent;

export type AuthAnalyticsSurface = 'anonymous_wall' | 'scan_result';

const AUTH_SURFACE_KEY = 'checkcoa.auth.surface';

/** Keep registration attribution across OAuth and magic-link page redirects. */
export const rememberAuthSurface = (surface?: AuthAnalyticsSurface) => {
    if (!surface) return;
    try {
        window.sessionStorage.setItem(AUTH_SURFACE_KEY, surface);
    } catch {
        // Storage can be unavailable in privacy modes; auth must still continue.
    }
};

/** Read and clear the pending registration surface after an auth redirect. */
export const consumeAuthSurface = (): AuthAnalyticsSurface | undefined => {
    try {
        const value = window.sessionStorage.getItem(AUTH_SURFACE_KEY);
        window.sessionStorage.removeItem(AUTH_SURFACE_KEY);
        return value === 'anonymous_wall' || value === 'scan_result' ? value : undefined;
    } catch {
        return undefined;
    }
};

/** Resolve the GA client_id (for server-side Measurement Protocol stitching). */
const getGaField = (field: 'client_id' | 'session_id'): Promise<string | null> =>
    new Promise((resolve) => {
        try {
            const gtag = (window as any).gtag;
            if (typeof gtag !== 'function') return resolve(null);
            gtag('get', 'G-RVC94QJ5CP', field, (value: string | number) => resolve(value ? String(value) : null));
            // Safety timeout in case the callback never fires.
            setTimeout(() => resolve(null), 800);
        } catch {
            resolve(null);
        }
    });

export const getGaClientId = (): Promise<string | null> => getGaField('client_id');

export const getGaAttribution = async (): Promise<{ clientId: string | null; sessionId: string | null }> => {
    const [clientId, sessionId] = await Promise.all([
        getGaField('client_id'),
        getGaField('session_id'),
    ]);
    return { clientId, sessionId };
};
